import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Dropdown, Row, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  EllipsisOutlined,
  EditFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import { isEqual } from "lodash";

import CommonTable from "../../../common/CommonTable";
import EditRole from "./EditRole";
import DeleteRole from "./DeleteRole";
import DraggableList from "../../../common/DraggableList";
import {
  getAllRoles,
  permissionUpdate,
} from "../../../actionCreator/roleManagementActionCreator";
import { useAlert } from "../../../context/AlertProvider";

const RoleBasedPermissions = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [active, setActive] = useState("ADMIN");
  const [role, setRole] = useState();
  const [isDelete, setDelete] = useState();
  const roles = useSelector((state) => state.roleManagementReducer.roles);
  const loading = useSelector((state) => state.roleManagementReducer.loading);
  const [roleData, setRoles] = useState([]);

  const handleUpdate = async (payload, isSuccess = false) => {
    if (loading) return;
    const res = await dispatch(permissionUpdate(payload));
    dispatch(getAllRoles());
    if (res.status !== 200) {
      showAlert("error", "Failed to update permissions. Please try again.");
    }
    if (res.status === 200 && isSuccess) {
      showAlert("success", "Permissions updated successfully.");
    }
  };

  useEffect(() => {
    if (roles?.roles) {
      setRoles(roles.roles);
    }
  }, [roles]);

  const handleChecked = (v, row, action, id) => {
    setRoles(
      [
        ...roleData?.filter((d) => d?.name !== active),
        {
          ...roleData?.find((v) => v?.name === active),
          permissions: {
            ...roleData?.find((v) => v?.name === active)?.permissions,
            [row?.name]: v.target.checked
              ? [
                  ...roleData?.find((v) => v?.name === active)?.permissions?.[
                    row?.name
                  ],
                  {
                    createdDate: new Date(),
                    id: id,
                    internalName: null,
                    lastModifiedDate: new Date(),
                    name: action,
                  },
                ]
              : [
                  ...roleData
                    ?.find((v) => v?.name === active)
                    ?.permissions?.[
                      row?.name
                    ]?.filter((d) => d?.name !== action),
                ],
          },
        },
      ].sort((a, b) => a.position - b.position)
    );
  };

  const renderRoleCard = (role, index) => {
    const items = [
      {
        key: "1",
        label: (
          <Row
            gutter={24}
            className="heading14px"
            style={{ color: "#29292B" }}
            onClick={() => setRole(role)}
          >
            <Col span={6}>
              <EditFilled />
            </Col>
            <Col span={18}>Edit</Col>
          </Row>
        ),
      },
      {
        key: "2",
        label: (
          <Row
            gutter={24}
            style={{ color: "#29292B" }}
            onClick={() => setDelete(role)}
          >
            <Col span={6}>
              <DeleteOutlined className="heading14px text-error" />
            </Col>

            <Col span={18} className="heading14px text-error">
              Delete
            </Col>
          </Row>
        ),
      },
    ].filter(Boolean);

    return (
      <Tooltip
        title={
          !index
            ? "Admin role cannot be reordered due to highest privilege."
            : null
        }
      >
        <div
          style={{ height: 32 }}
          className={`cursor-pointer white-space-nowrap d-flex text-capitalize align-items-center rounded gap-10 px-10 text-color-6E6E6E ${active?.toLowerCase() === role?.name?.toLowerCase() && "bg-white text-theme"}`}
          onClick={() => setActive(role?.name)}
        >
          {role?.name}
          {role?.name?.toLowerCase() !== "admin" && (
            <Dropdown menu={{ items }} trigger={["click"]}>
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          )}
        </div>
      </Tooltip>
    );
  };

  const tooltipText = "Admin permissions are fixed and non-editable.";
  const isAdmin = active?.toLowerCase() === "admin";

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "View (V)",
      dataIndex: "permissions",
      key: "permissions",
      render: (record, row) => {
        return (
          <Tooltip title={isAdmin ? tooltipText : null}>
            <Checkbox
              checked={record?.isView}
              disabled={isAdmin}
              onChange={(v) => {
                handleChecked(v, row, "View", 1);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Edit (E)",
      dataIndex: "permissions",
      key: "permissions",
      render: (record, row) => {
        return (
          <Tooltip title={isAdmin ? tooltipText : null}>
            <Checkbox
              checked={
                ["Company", "Executive"].includes(row?.name)
                  ? false
                  : record?.isEdit
              }
              disabled={isAdmin || ["Company", "Executive"].includes(row?.name)}
              onChange={(v) => handleChecked(v, row, "Edit", 2)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Create (C)",
      dataIndex: "permissions",
      key: "permissions",
      render: (record, row) => {
        return (
          <Tooltip title={isAdmin ? tooltipText : null}>
            <Checkbox
              checked={
                ["Company", "Executive"].includes(row?.name)
                  ? false
                  : record?.isCreate
              }
              disabled={isAdmin || ["Company", "Executive"].includes(row?.name)}
              onChange={(v) => {
                handleChecked(v, row, "Create", 3);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Delete (D)",
      dataIndex: "permissions",
      key: "permissions",
      render: (record, row) => {
        return (
          <Tooltip title={isAdmin ? tooltipText : null}>
            <Checkbox
              checked={
                ["Company", "Executive"].includes(row?.name)
                  ? false
                  : record?.isDelete
              }
              disabled={isAdmin || ["Company", "Executive"].includes(row?.name)}
              onChange={(v) => {
                handleChecked(v, row, "Delete", 4);
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const activeData = roleData?.find(
    (role) => role?.name === active
  )?.permissions;

  const data = activeData
    ? Object.entries(activeData).map(([moduleName, permissions]) => {
        const mappedPermissions = {
          isView: permissions?.length
            ? permissions.some((p) => p?.name === "View")
            : false,
          isEdit: permissions?.length
            ? permissions.some((p) => p?.name === "Edit")
            : false,
          isCreate: permissions?.length
            ? permissions.some((p) => p?.name === "Create")
            : false,
          isDelete: permissions?.length
            ? permissions.some((p) => p?.name === "Delete")
            : false,
        };
        return {
          name: moduleName,
          permissions: mappedPermissions,
        };
      })
    : [];

  return (
    <Card
      title={
        <div className="text-theme heading18px">Role-Based Permissions</div>
      }
      className="rounded-2xl shadow-md"
    >
      {!!roleData?.length && (
        <div
          className="d-flex align-items-center gap-20 bg-muted p5px rounded mb20"
          style={{ overflowX: "auto", overflowY: "hidden" }}
        >
          <DraggableList
            items={roleData}
            onChange={(val) => {
              setRoles(val);
              handleUpdate({ roles: val?.filter((d) => d?.name !== "ADMIN") });
            }}
            renderItem={renderRoleCard}
          />
        </div>
      )}
      <CommonTable
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 50 }}
        rowClassName={(record) =>
          active?.toLowerCase() === "admin" ? "greyed-out-row" : ""
        }
      />
      <div className="d-flex justify-content-end">
        <Button
          type="primary"
          disabled={active === "ADMIN" || isEqual(roleData, roles?.roles)}
          className="theme-btn mt30"
          onClick={() =>
            handleUpdate(
              {
                roles: roleData?.filter((d) => d?.name !== "ADMIN"),
              },
              true
            )
          }
        >
          Save Permissions
        </Button>
      </div>
      <DeleteRole
        isOpen={isDelete}
        callback={() => setActive("ADMIN")}
        handleCancel={() => setDelete()}
      />
      <EditRole
        role={role}
        isOpen={!!role}
        closeModal={() => setRole()}
        callback={(val) => setActive(val)}
      />
    </Card>
  );
};

export default RoleBasedPermissions;
